// src/components/sacred/Lawh.jsx
// "Lawḥ" (slate) card — the core surface for tabular/grouped info.
// No borders, 16px radius, surface-container-highest bg,
// optional 8-pointed zellige star watermark in bottom-left.
//
// S10 — when an `onClick` is supplied (and no explicit role), the
// component opts into `role="button"` + `tabIndex=0` + Enter/Space
// keyboard activation so it remains operable without a mouse. This
// keeps the brand surface (a card) usable as a button without
// shoehorning it into MUI's Button visual language.

import React from 'react';
import { Box } from '@mui/material';
import { radii, space, elevation } from '../../theme/sacredCanvas';

const Lawh = React.forwardRef(function Lawh(
  {
    children,
    elevated = false,
    watermark = false,
    tone = 'highest',
    sx = {},
    onClick,
    onKeyDown,
    role,
    tabIndex,
    ...rest
  },
  ref,
) {
  const toneToBg = {
    lowest: (t) => t.palette.sacred.surfaceContainerLowest,
    low: (t) => t.palette.sacred.surfaceContainerLow,
    default: (t) => t.palette.sacred.surfaceContainer,
    high: (t) => t.palette.sacred.surfaceContainerHigh,
    highest: (t) => t.palette.sacred.surfaceContainerHighest,
    primary: (t) => t.palette.sacred.primary,
  };

  const isInteractive = typeof onClick === 'function';
  const resolvedRole = role ?? (isInteractive ? 'button' : undefined);
  const resolvedTabIndex = tabIndex ?? (isInteractive ? 0 : undefined);
  const handleKeyDown = (e) => {
    if (onKeyDown) onKeyDown(e);
    if (e.defaultPrevented) return;
    if (isInteractive && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      onClick(e);
    }
  };

  return (
    <Box
      ref={ref}
      className={watermark ? 'zellige-watermark' : undefined}
      onClick={onClick}
      onKeyDown={isInteractive ? handleKeyDown : onKeyDown}
      role={resolvedRole}
      tabIndex={resolvedTabIndex}
      sx={(t) => ({
        position: 'relative',
        backgroundColor: toneToBg[tone](t),
        color: tone === 'primary' ? t.palette.sacred.onPrimary : t.palette.sacred.onSurface,
        borderRadius: `${radii.xl}px`,
        padding: { xs: `${space[6]}px`, md: `${space[7]}px` },
        boxShadow: elevated ? elevation.float : elevation.flat,
        transition: 'transform 220ms ease, box-shadow 220ms ease',
        overflow: 'hidden',
        ...(isInteractive && {
          cursor: 'pointer',
          '&:focus-visible': {
            outline: `2px solid ${t.palette.sacred.primary}`,
            outlineOffset: 2,
          },
        }),
        ...sx,
      })}
      {...rest}
    >
      {children}
    </Box>
  );
});

export default Lawh;
